import { invalidateProjectFinancials } from "@/lib/invalidateProjectFinancials";
import { memo, useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type HTMLAttributes } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useInvoices, type InvoiceFilterStatus } from "@/hooks/useInvoices";
import { usePagination } from "@/hooks/usePagination";
import { PaginationControls } from "@/components/controller/PaginationControls";
import { handleRpcError } from "@/lib/rpcErrors";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DataTable } from "@/components/shared/DataTable";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { format } from "date-fns";
import { ExportToolbar } from "@/components/shared/ExportToolbar";
import { exportPdf } from "@/lib/export";
import { InvoicePrintTemplate } from "@/components/shared/print-templates/InvoicePrintTemplate";
import { Upload, AlertTriangle, Trash2, Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import { type ColumnDef } from "@tanstack/react-table";
import { InvoiceUploadDialog } from "@/components/controller/InvoiceUploadDialog";
import { InvoiceDetailDrawer } from "@/components/controller/InvoiceDetailDrawer";
import { useCompanySelector } from "@/hooks/useCompanySelector";
import {
  formatQbDate,
  fmtCurrency,
  QbStatusCell,
  QbActionLinks,
  QbTypeBadge,
} from "./invoiceQbHelpers";

const DEFAULT_INVOICE_FILTERS: InvoiceColumnFilterState = {
  date: "",
  type: "all",
  number: "",
  customer: "",
  memo: "",
  amount: "",
  status: "all",
};

type ColumnFilterRendererMap = Record<string, React.ReactNode>;

type InvoiceColumnFilterState = {
  date: string;
  type: "all" | "qb" | "manual";
  number: string;
  customer: string;
  memo: string;
  amount: string;
  status: InvoiceFilterStatus;
};

const TYPE_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "qb", label: "QB" },
  { value: "manual", label: "Manual" },
] as const;

const STATUS_FILTER_OPTIONS: Array<{ value: InvoiceFilterStatus; label: string }> = [
  { value: "all", label: "All" },
  { value: "needs_review", label: "Needs Review" },
  { value: "draft", label: "Draft" },
  { value: "approved", label: "Approved" },
  { value: "sent", label: "Sent" },
  { value: "partially_paid", label: "Partially Paid" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
  { value: "void", label: "Void" },
];

const ColumnTextFilter = memo(function ColumnTextFilter({
  value,
  onChange,
  placeholder = "Filter…",
  className,
  type = "text",
  inputMode,
}: {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  type?: string;
  inputMode?: HTMLAttributes<HTMLInputElement>["inputMode"];
}) {
  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  }, [onChange]);

  return (
    <Input
      type={type}
      inputMode={inputMode}
      placeholder={placeholder}
      value={value ?? ""}
      onChange={handleChange}
      className={className ?? "h-7 text-xs"}
    />
  );
});

const ColumnSelectFilter = memo(function ColumnSelectFilter<TValue extends string>({
  value,
  onChange,
  options,
}: {
  value: TValue;
  onChange: (value: TValue) => void;
  options: ReadonlyArray<{ value: TValue; label: string }>;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-7 text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
});

const ColumnDateFilter = memo(function ColumnDateFilter({
  value,
  onChange,
}: {
  value?: string;
  onChange: (value: string) => void;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("h-7 w-full justify-start text-left font-normal text-xs", !value && "text-muted-foreground")}
        >
          <CalendarIcon className="mr-1.5 h-3 w-3" />
          {value ? format(new Date(`${value}T00:00:00`), "MMM d, yyyy") : "Date…"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value ? new Date(`${value}T00:00:00`) : undefined}
          onSelect={(date) => onChange(date ? format(date, "yyyy-MM-dd") : "")}
          initialFocus
          className={cn("p-3 pointer-events-auto")}
        />
        {value && (
          <div className="border-t p-2">
            <Button size="sm" variant="ghost" className="h-7 w-full" onClick={() => onChange("")}>
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
});

export const InvoiceTable = () => {
  const isMobile = useIsMobile();
  const { activeCompanyId } = useCompanySelector();
  const [draftFilters, setDraftFilters] = useState<InvoiceColumnFilterState>(DEFAULT_INVOICE_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<InvoiceColumnFilterState>(DEFAULT_INVOICE_FILTERS);
  const pagination = usePagination();
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateDraftFilter = useCallback(<K extends keyof InvoiceColumnFilterState>(key: K, value: InvoiceColumnFilterState[K]) => {
    setDraftFilters((current) => (current[key] === value ? current : { ...current, [key]: value }));
  }, []);

  const filters = useMemo(() => ({
    invoiceNumber: appliedFilters.number,
    customer: appliedFilters.customer,
    memo: appliedFilters.memo,
    date: appliedFilters.date || undefined,
    amountMin: appliedFilters.amount === "" ? undefined : Number(appliedFilters.amount),
    type: appliedFilters.type === "all" ? undefined : (appliedFilters.type === "qb" ? "qb_import" as const : "manual" as const),
  }), [appliedFilters]);

  useEffect(() => {
    if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    filterDebounceRef.current = setTimeout(() => {
      setAppliedFilters((current) => {
        const next = draftFilters;
        return current.date === next.date &&
          current.type === next.type &&
          current.number === next.number &&
          current.customer === next.customer &&
          current.memo === next.memo &&
          current.amount === next.amount &&
          current.status === next.status
          ? current
          : next;
      });
    }, 250);

    return () => {
      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    };
  }, [draftFilters]);

  useEffect(() => {
    pagination.setPage(1);
  }, [appliedFilters, pagination]);

  const { data: rows, isLoading } = useInvoices(appliedFilters.status, pagination, undefined, filters);
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

  const [paymentDialog, setPaymentDialog] = useState<{ id: string; total: number; amountPaid: number } | null>(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentRef, setPaymentRef] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");

  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteBusy, setBulkDeleteBusy] = useState(false);

  const invalidateAll = () => {
    // Broad invalidation — list view spans many projects.
    invalidateProjectFinancials(qc);
  };

  const approve = async (id: string) => {
    setBusy(id);
    try {
      const { error } = await supabase.rpc("approve_invoice", { p_invoice_id: id });
      if (error) throw error;
      toast.success("Invoice approved");
      invalidateAll();
    } catch (err) { handleRpcError(err); } finally { setBusy(null); }
  };

  const markSent = async (id: string) => {
    setBusy(id);
    try {
      const { error } = await supabase.rpc("transition_invoice_status", { p_invoice_id: id, p_new_status: "sent" });
      if (error) throw error;
      toast.success("Invoice marked as sent");
      invalidateAll();
    } catch (err: any) { handleRpcError(err); } finally { setBusy(null); }
  };

  const recordPayment = async () => {
    if (!paymentDialog) return;
    const amount = Number(paymentAmount);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    setBusy(paymentDialog.id);
    try {
      const { data, error } = await supabase.rpc("transition_invoice_status", {
        p_invoice_id: paymentDialog.id, p_new_status: "paid",
        p_payment_amount: amount, p_payment_ref: paymentRef || null, p_payment_method: paymentMethod || null,
      });
      if (error) throw error;
      const newStatus = (data as any)?.new_status;
      toast.success(newStatus === "paid" ? "Invoice paid in full" : "Partial payment recorded");
      invalidateAll();
      setPaymentDialog(null); setPaymentAmount(""); setPaymentRef(""); setPaymentMethod("");
    } catch (err: any) { handleRpcError(err); } finally { setBusy(null); }
  };

  const bulkDeleteQbInvoices = async () => {
    if (!activeCompanyId) return;
    setBulkDeleteBusy(true);
    try {
      const { data, error } = await supabase.rpc("bulk_soft_delete_qb_invoices", { p_company_id: activeCompanyId });
      if (error) throw error;
      toast.success(`${data} QB-imported invoice(s) deleted`);
      invalidateAll();
      setBulkDeleteOpen(false);
    } catch (err: any) { handleRpcError(err); } finally { setBulkDeleteBusy(false); }
  };

  const printInvoice = async (inv: any) => {
    const { data: lines } = await supabase.from("invoice_lines").select("description, quantity, unit_price, line_total").eq("invoice_id", inv.id);
    exportPdf(
      <InvoicePrintTemplate data={{
        invoice_number: inv.invoice_number, invoice_date: inv.invoice_date, due_date: inv.due_date,
        status: inv.status, total: inv.total, amount_paid: inv.amount_paid || 0,
        payment_method: inv.payment_method, payment_reference: inv.payment_reference,
        customer_name: (inv.projects as any)?.customers?.name, project_name: (inv.projects as any)?.name,
        notes: inv.notes,
        line_items: (lines ?? []).map((l: any) => ({ description: l.description, quantity: l.quantity, unit_price: l.unit_price, line_total: l.line_total })),
      }} />,
      `Invoice ${inv.invoice_number}`
    );
  };

  const buildHandlers = useCallback((inv: any) => ({
    onEdit: () => setSelectedInvoiceId(inv.id),
    onApprove: inv.status === "draft" ? () => approve(inv.id) : undefined,
    onSend: inv.status === "approved" ? () => markSent(inv.id) : undefined,
    onReceivePayment: inv.source !== "qb_import" && ["approved", "sent", "partially_paid", "overdue"].includes(inv.status)
      ? () => setPaymentDialog({ id: inv.id, total: inv.total, amountPaid: inv.amount_paid || 0 })
      : undefined,
    onPrint: () => printInvoice(inv),
    busy: busy === inv.id,
  }), [busy]);

  const columns = useMemo<ColumnDef<any, any>[]>(() => [
    {
      accessorKey: "invoice_date",
      header: "DATE",
      cell: ({ getValue }) => <span className="text-sm">{formatQbDate(getValue() as string)}</span>,
    },
    {
      id: "type",
      header: "TYPE",
      enableSorting: false,
      cell: ({ row }) => <QbTypeBadge isQb={row.original.source === "qb_import"} />,
    },
    {
      accessorKey: "invoice_number",
      header: "NO.",
      cell: ({ row }) => {
        const inv = row.original;
        const needsReview = inv.needs_customer_assignment || inv.needs_project_assignment;
        return (
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{inv.invoice_number}</span>
            {needsReview && (
              <Badge variant="outline" className="h-5 px-1.5 border-amber-500 text-amber-700 dark:text-amber-400 text-[10px] gap-1">
                <AlertTriangle className="h-3 w-3" />
                Needs Assignment
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      id: "customer", header: "CUSTOMER",
      accessorFn: (row: any) => (row.customers as any)?.name ?? "—",
    },
    {
      id: "memo",
      header: "MEMO",
      accessorFn: (row: any) => row.notes ?? "",
      cell: ({ getValue }) => {
        const v = (getValue() as string) || "";
        return <span className="text-sm text-muted-foreground line-clamp-1 max-w-xs" title={v}>{v || "—"}</span>;
      },
    },
    {
      accessorKey: "total",
      header: () => <div className="text-right">AMOUNT</div>,
      cell: ({ getValue }) => <div className="text-right font-medium text-primary">{fmtCurrency(getValue() as number)}</div>,
    },
    {
      accessorKey: "status",
      header: "STATUS",
      cell: ({ row }) => <QbStatusCell inv={row.original} />,
    },
    {
      id: "actions", header: "ACTION", enableSorting: false, enableColumnFilter: false,
      cell: ({ row }) => <QbActionLinks inv={row.original} handlers={buildHandlers(row.original)} />,
    },
  ], [buildHandlers]);

  const columnFilterRenderers = useMemo<ColumnFilterRendererMap>(() => ({
    invoice_date: (
      <ColumnDateFilter
        value={draftFilters.date}
        onChange={(value) => updateDraftFilter("date", value)}
      />
    ),
    type: (
      <ColumnSelectFilter
        value={draftFilters.type}
        onChange={(value) => updateDraftFilter("type", value as InvoiceColumnFilterState["type"])}
        options={TYPE_FILTER_OPTIONS}
      />
    ),
    invoice_number: (
      <ColumnTextFilter
        value={draftFilters.number ?? ""}
        onChange={(value) => updateDraftFilter("number", value)}
      />
    ),
    customer: (
      <ColumnTextFilter
        value={draftFilters.customer ?? ""}
        onChange={(value) => updateDraftFilter("customer", value)}
      />
    ),
    memo: (
      <ColumnTextFilter
        value={draftFilters.memo ?? ""}
        onChange={(value) => updateDraftFilter("memo", value)}
      />
    ),
    total: (
      <ColumnTextFilter
        type="number"
        inputMode="decimal"
        placeholder="≥ amount"
        value={draftFilters.amount ?? ""}
        onChange={(value) => updateDraftFilter("amount", value)}
        className="h-7 text-xs text-right"
      />
    ),
    status: (
      <ColumnSelectFilter
        value={draftFilters.status}
        onChange={(value) => updateDraftFilter("status", value as InvoiceColumnFilterState["status"])}
        options={STATUS_FILTER_OPTIONS}
      />
    ),
  }), [draftFilters.amount, draftFilters.customer, draftFilters.date, draftFilters.memo, draftFilters.number, draftFilters.status, draftFilters.type, updateDraftFilter]);

  const renderColumnFilter = useCallback((columnId: string) => columnFilterRenderers[columnId] ?? null, [columnFilterRenderers]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {rows && rows.length > 0 && <ExportToolbar entityType="invoices" data={rows} filterDescription={`status: ${appliedFilters.status}`} />}
        <Button size="sm" variant="outline" onClick={() => setUploadOpen(true)}>
          <Upload className="mr-1 h-4 w-4" />Upload Invoice
        </Button>
        <Button size="sm" variant="destructive" onClick={() => setBulkDeleteOpen(true)}>
          <Trash2 className="mr-1 h-4 w-4" />Delete QB Imports
        </Button>
      </div>




      {isMobile ? (
        <>
          {isLoading ? <p className="p-4 text-muted-foreground">Loading…</p> : !rows?.length ? <p className="p-4 text-muted-foreground">No invoices found.</p> : (
            <div className="space-y-3">
              {rows.map((inv: any) => (
                <Card key={inv.id} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setSelectedInvoiceId(inv.id)}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-muted-foreground">{formatQbDate(inv.invoice_date)}</span>
                          <QbTypeBadge isQb={inv.source === "qb_import"} />
                          <span className="font-medium text-sm">{inv.invoice_number}</span>
                          {(inv.needs_customer_assignment || inv.needs_project_assignment) && (
                            <Badge variant="outline" className="h-5 px-1.5 border-amber-500 text-amber-700 dark:text-amber-400 text-[10px] gap-1">
                              <AlertTriangle className="h-3 w-3" />Needs Assignment
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium mt-1 truncate">{(inv.customers as any)?.name ?? "—"}</p>
                        {inv.notes && <p className="text-xs text-muted-foreground line-clamp-1">{inv.notes}</p>}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-lg font-bold text-primary">{fmtCurrency(inv.total)}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 pt-1">
                      <QbStatusCell inv={inv} />
                      <QbActionLinks inv={inv} handlers={buildHandlers(inv)} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          <PaginationControls pagination={pagination} />
        </>
      ) : (
        <>
          <DataTable
            tableId="invoices-list"
            columns={columns}
            data={rows ?? []}
            isLoading={isLoading}
            emptyMessage="No invoices found."
            onRowClick={(row: any) => setSelectedInvoiceId(row.id)}
            serverFiltered
            enableFiltering={false}
            alwaysShowColumnFilters
            renderColumnFilter={renderColumnFilter}
          />
          <PaginationControls pagination={pagination} />
        </>
      )}

      {/* Payment Dialog */}
      <Dialog open={!!paymentDialog} onOpenChange={(o) => { if (!o) setPaymentDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>{paymentDialog && `Remaining: ${fmtCurrency(paymentDialog.total - paymentDialog.amountPaid)}`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Amount</Label><Input type="number" placeholder="0.00" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} /></div>
            <div className="space-y-1">
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="check">Check</SelectItem><SelectItem value="ach">ACH</SelectItem>
                  <SelectItem value="wire">Wire</SelectItem><SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem><SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Reference (optional)</Label><Input placeholder="Check #, ACH ref, etc." value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialog(null)}>Cancel</Button>
            <Button disabled={busy !== null} onClick={recordPayment}>{busy ? "Saving…" : "Record"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete QB Imports Confirmation */}
      <Dialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete All QB-Imported Invoices</DialogTitle>
            <DialogDescription>
              This will soft-delete all invoices that were imported from QuickBooks. They can be recovered if needed. Are you sure?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteOpen(false)}>Cancel</Button>
            <Button variant="destructive" disabled={bulkDeleteBusy} onClick={bulkDeleteQbInvoices}>
              {bulkDeleteBusy ? "Deleting…" : "Delete All QB Imports"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InvoiceUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onSuccess={invalidateAll}
      />

      <InvoiceDetailDrawer
        invoiceId={selectedInvoiceId}
        open={!!selectedInvoiceId}
        onOpenChange={(o) => { if (!o) setSelectedInvoiceId(null); }}
        onMutate={invalidateAll}
      />
    </div>
  );
};
